import BackendService from 'BackendService';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { SimplifiedFundUpdateEditorHandle } from './SimplifiedFundUpdateEditor.types';
import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import {
  createInitialFundUpdatesAdminState,
  type FundUpdatesAdminPanelProps,
  type FundUpdatesAdminState,
} from './fundUpdatesAdmin.types';
import { todayDateString } from './fundUpdatesAdminUtils';
import { useFundUpdateHighlights } from './useFundUpdateHighlights';
import { useFundUpdateDeletion } from './useFundUpdateDeletion';
import { useFundUpdateDraftCreation } from './useFundUpdateDraftCreation';
import { useFundUpdateTitle } from './useFundUpdateTitle';

function useFundUpdateCmsRefs() {
  const cmsRefs = useRef(new Map<string, React.RefObject<SimplifiedFundUpdateEditorHandle>>());

  const getCmsRef = useCallback((id: string): React.RefObject<SimplifiedFundUpdateEditorHandle> => {
    if (!cmsRefs.current.has(id)) {
      cmsRefs.current.set(id, React.createRef<SimplifiedFundUpdateEditorHandle>());
    }
    return cmsRefs.current.get(id)!;
  }, []);

  return { getCmsRef };
}

export function useFundUpdatesAdmin({
  fundOpportunityId,
}: Pick<FundUpdatesAdminPanelProps, 'fundOpportunityId'>) {
  const [state, setState] = useState<FundUpdatesAdminState>(createInitialFundUpdatesAdminState);
  const { getCmsRef } = useFundUpdateCmsRefs();

  const loadUpdates = useCallback(async () => {
    try {
      const entries: AdminFundUpdateEntry[] = await (
        new BackendService() as BackendService & {
          getAdminFundUpdates: (id: string) => Promise<AdminFundUpdateEntry[]>;
        }
      ).getAdminFundUpdates(fundOpportunityId);
      const drafts = entries.filter((e) => e.status === 'draft');
      const published = entries.filter((e) => e.status === 'published');
      setState((prev) => ({ ...prev, loading: false, drafts, published }));
    } catch {
      setState((prev) => ({ ...prev, loading: false, error: 'Unable to load fund updates.' }));
    }
  }, [fundOpportunityId]);

  useEffect(() => {
    void loadUpdates();
  }, [loadUpdates]);

  const handleCmsBusyChange = useCallback((id: string, busy: boolean) => {
    setState((prev) => {
      // The caller (SimplifiedFundUpdateEditor's onBusyChange effect) depends on
      // its own onBusyChange prop, which is a fresh closure every render of its
      // caller — without this bail-out, every render would produce a "new" busy
      // state here even when nothing changed, triggering another render, forever.
      if (prev.cmsBusyIds.has(id) === busy) return prev;
      const cmsBusyIds = new Set(prev.cmsBusyIds);
      if (busy) cmsBusyIds.add(id);
      else cmsBusyIds.delete(id);
      return { ...prev, cmsBusyIds };
    });
  }, []);

  const { handleCreateDraft, handleDuplicateUpdate } = useFundUpdateDraftCreation(
    fundOpportunityId,
    state.newDraftDate,
    setState,
  );

  const handleDraftDateChange = useCallback(
    async (draft: AdminFundUpdateEntry, newDate: string) => {
      setState((prev) => {
        const savingDateIds = new Set(prev.savingDateIds);
        savingDateIds.add(draft.externalId);
        return { ...prev, savingDateIds };
      });
      try {
        const updated = await (
          new BackendService() as BackendService & {
            setFundUpdateDate: (
              fundId: string,
              updateId: string,
              date: string,
            ) => Promise<AdminFundUpdateEntry>;
          }
        ).setFundUpdateDate(fundOpportunityId, draft.externalId, newDate);
        setState((prev) => {
          const savingDateIds = new Set(prev.savingDateIds);
          savingDateIds.delete(draft.externalId);
          return {
            ...prev,
            savingDateIds,
            drafts: prev.drafts.map((d) =>
              d.externalId === draft.externalId ? { ...d, ...updated, updateDate: newDate } : d,
            ),
          };
        });
      } catch {
        setState((prev) => {
          const savingDateIds = new Set(prev.savingDateIds);
          savingDateIds.delete(draft.externalId);
          return { ...prev, savingDateIds };
        });
      }
    },
    [fundOpportunityId],
  );

  const handlePublishDraft = useCallback(
    async (draft: AdminFundUpdateEntry) => {
      setState((prev) => ({ ...prev, publishing: true, error: null }));
      try {
        const backendService = new BackendService() as BackendService & {
          setFundUpdateDate: (
            fundId: string,
            updateId: string,
            date: string,
          ) => Promise<AdminFundUpdateEntry>;
          publishFundUpdate: (fundId: string, updateId: string) => Promise<void>;
        };
        // A draft's date is set when it's created, but it may sit unpublished
        // for a while — roll it to today so the date shown in the app reflects
        // when it actually went live, not when the draft was first started.
        await backendService.setFundUpdateDate(
          fundOpportunityId,
          draft.externalId,
          todayDateString(),
        );
        await backendService.publishFundUpdate(fundOpportunityId, draft.externalId);
        await loadUpdates();
        setState((prev) => ({ ...prev, publishing: false }));
      } catch (err) {
        console.error('[FundUpdatesAdminPanel] publishDraft failed:', err);
        setState((prev) => ({
          ...prev,
          publishing: false,
          error: 'Unable to publish update. Please try again.',
        }));
      }
    },
    [fundOpportunityId, loadUpdates],
  );

  const handlePublishEditedUpdate = useCallback(
    async (update: AdminFundUpdateEntry) => {
      setState((prev) => ({ ...prev, publishing: true, error: null }));
      try {
        // Already-published updates (including legacy rows) never carry status
        // 'draft', so the draft-only FundUpdatesController.publish endpoint always
        // rejects them. Re-publishing edited content on a live update instead goes
        // through the classic, status-agnostic content-management publish path —
        // the same update entry the published list uses.
        await new BackendService().publishContentManagement('FundUpdate', update.externalId);
        await loadUpdates();
        setState((prev) => ({ ...prev, publishing: false }));
      } catch (err) {
        console.error('[FundUpdatesAdminPanel] publishEditedUpdate failed:', err);
        setState((prev) => ({
          ...prev,
          publishing: false,
          error: 'Unable to publish update. Please try again.',
        }));
      }
    },
    [loadUpdates],
  );

  const handleRevertToPublished = useCallback(
    async (update: AdminFundUpdateEntry) => {
      setState((prev) => ({ ...prev, reverting: true, error: null }));
      try {
        await new BackendService().revertContentManagement('FundUpdate', update.externalId);
        await loadUpdates();
        setState((prev) => ({
          ...prev,
          reverting: false,
          revertNonce: {
            ...prev.revertNonce,
            [update.externalId]: (prev.revertNonce[update.externalId] ?? 0) + 1,
          },
        }));
      } catch (err) {
        console.error('[FundUpdatesAdminPanel] revertToPublished failed:', err);
        setState((prev) => ({
          ...prev,
          reverting: false,
          error: 'Unable to revert to the published version. Please try again.',
        }));
      }
    },
    [loadUpdates],
  );

  const handlePublishAll = useCallback(async () => {
    setState((prev) => ({ ...prev, publishingAll: true, error: null }));
    try {
      const backendService = new BackendService() as BackendService & {
        setFundUpdateDate: (
          fundId: string,
          updateId: string,
          date: string,
        ) => Promise<AdminFundUpdateEntry>;
        publishFundUpdate: (fundId: string, updateId: string) => Promise<void>;
      };
      const today = todayDateString();
      const outcomes = await Promise.allSettled([
        ...state.drafts.map(async (draft) => {
          await backendService.setFundUpdateDate(fundOpportunityId, draft.externalId, today);
          await backendService.publishFundUpdate(fundOpportunityId, draft.externalId);
        }),
        ...state.published
          .filter((p) => p.hasPendingDraft)
          .map((update) =>
            backendService.publishContentManagement('FundUpdate', update.externalId),
          ),
      ]);
      await loadUpdates();
      const anyFailed = outcomes.some((o) => o.status === 'rejected');
      setState((prev) => ({
        ...prev,
        publishingAll: false,
        error: anyFailed ? 'Some updates could not be published. Please try again.' : prev.error,
      }));
    } catch (err) {
      console.error('[FundUpdatesAdminPanel] publishAll failed:', err);
      setState((prev) => ({
        ...prev,
        publishingAll: false,
        error: 'Unable to publish all updates. Please try again.',
      }));
    }
  }, [fundOpportunityId, loadUpdates, state.drafts, state.published]);

  const handleRevertAllToPublished = useCallback(async () => {
    setState((prev) => ({ ...prev, revertingAll: true, error: null }));
    try {
      const backendService = new BackendService();
      const pendingPublished = state.published.filter((p) => p.hasPendingDraft);
      const outcomes = await Promise.allSettled([
        ...state.drafts.map((draft) =>
          backendService.discardFundUpdate(fundOpportunityId, draft.externalId),
        ),
        ...pendingPublished.map((update) =>
          backendService.revertContentManagement('FundUpdate', update.externalId),
        ),
      ]);
      await loadUpdates();
      const anyFailed = outcomes.some((o) => o.status === 'rejected');
      setState((prev) => ({
        ...prev,
        revertingAll: false,
        error: anyFailed ? 'Some updates could not be reverted. Please try again.' : prev.error,
        revertNonce: pendingPublished.reduce(
          (acc, update) => ({
            ...acc,
            [update.externalId]: (prev.revertNonce[update.externalId] ?? 0) + 1,
          }),
          prev.revertNonce,
        ),
      }));
    } catch (err) {
      console.error('[FundUpdatesAdminPanel] revertAllToPublished failed:', err);
      setState((prev) => ({
        ...prev,
        revertingAll: false,
        error: 'Unable to revert all updates. Please try again.',
      }));
    }
  }, [fundOpportunityId, loadUpdates, state.drafts, state.published]);

  const { handleDeleteDraft, handleDeletePublished, handleConfirmDelete } = useFundUpdateDeletion(
    fundOpportunityId,
    state.deleteConfirm,
    setState,
  );

  const toggleExpanded = useCallback((externalId: string) => {
    setState((prev) => {
      const expandedIds = new Set(prev.expandedIds);
      if (expandedIds.has(externalId)) expandedIds.delete(externalId);
      else expandedIds.add(externalId);
      return { ...prev, expandedIds };
    });
  }, []);

  const toggleEditPublished = useCallback((externalId: string) => {
    setState((prev) => {
      const editingPublishedIds = new Set(prev.editingPublishedIds);
      if (editingPublishedIds.has(externalId)) editingPublishedIds.delete(externalId);
      else {
        editingPublishedIds.clear();
        editingPublishedIds.add(externalId);
      }
      return { ...prev, editingPublishedIds };
    });
  }, []);

  const { handleTitleChange } = useFundUpdateTitle(fundOpportunityId, setState);

  const highlightHandlers = useFundUpdateHighlights(fundOpportunityId, setState, getCmsRef, {
    highlightEdits: state.highlightEdits,
    highlightEditingId: state.highlightEditingId,
    highlightSuggestion: state.highlightSuggestion,
    generateConfirm: state.generateConfirm,
  });

  return {
    state,
    setState,
    getCmsRef,
    loadUpdates,
    handleCmsBusyChange,
    handleCreateDraft,
    handleDuplicateUpdate,
    handleDraftDateChange,
    handleTitleChange,
    handlePublishDraft,
    handlePublishEditedUpdate,
    handleRevertToPublished,
    handlePublishAll,
    handleRevertAllToPublished,
    handleDeleteDraft,
    handleDeletePublished,
    handleConfirmDelete,
    toggleExpanded,
    toggleEditPublished,
    ...highlightHandlers,
  };
}
